// GoogleReviews.jsx
import React, { useEffect, useState, useRef } from 'react';

// SVG de la "G" de Google en colores oficiales
const GoogleGLogo = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 533.5 544.3"
    style={{ marginLeft: '10px' }}
  >
    <path
      d="M533.5 278.4c0-17.4-1.5-34.3-4.6-50.7H272v95.6h146.9c-6.3 34-25.1 63-53.7 82.3v68h86.9c50.8-46.9 80.4-116 80.4-197.2z"
      fill="#4285F4"
    />
    <path
      d="M272 544.3c72.9 0 134-24.1 178.7-65.5l-86.9-68c-24.1 16.2-55.3 25.7-91.8 25.7-70.4 0-130.3-47.5-151.4-111.3H34v69.8C78.6 490 168.6 544.3 272 544.3z"
      fill="#34A853"
    />
    <path
      d="M120.6 324.1c-10.7-32-10.7-66.9 0-98.9v-69.8H34c-22.7 45.3-22.7 99.6 0 144.9l86.6-69.8z"
      fill="#FBBC05"
    />
    <path
      d="M272 107.6c39.6 0 75.3 13.7 103.6 40.6l77.7-77.7C406.3 24.1 344.2 0 272 0 168.6 0 78.6 54.3 34 136.5l86.6 69.8C141.7 155.1 201.6 107.6 272 107.6z"
      fill="#EA4335"
    />
  </svg>
);

// Arreglo de nombres sin números
const sampleNames = [
  "Alice", "Bob", "Charlie", "Diana", "Eve", "Frank", "Grace", "Heidi",
  "Ivan", "Judy", "Kathy", "Leo", "Megan", "Nate", "Olivia", "Paul",
  "Quinn", "Rachel", "Sam", "Trudy", "Uma", "Victor", "Wendy", "Xavier",
  "Yvonne", "Zach"
];

// Arreglo de comentarios variados
const sampleComments = [
  "Excellent service! Highly recommended.",
  "The driver was punctual and very professional.",
  "A truly luxurious experience; I felt safe and pampered.",
  "I appreciated the personalized service and attention to detail.",
  "Smooth and comfortable ride. The vehicle was immaculate.",
  "Exceptional service and reliability. My trip was stress-free.",
  "Friendly and courteous driver. Would use again!",
  "Outstanding experience from start to finish.",
  "The level of service exceeded my expectations.",
  "I felt very secure and well taken care of throughout my journey."
];

// Función para generar una fecha aleatoria en formato MM/DD/YYYY para un año dado
const getRandomDate = (year) => {
  let month, day;
  if (year === 2025) {
    // Para 2025, las fechas se generarán solo entre el 01/01/2025 y el 02/01/2025.
    month = Math.floor(Math.random() * 2) + 1; // 1 o 2
    if (month === 2) {
      day = 1; // Solo el 1 de febrero
    } else {
      // En enero se pueden generar días entre 1 y 31
      day = Math.floor(Math.random() * 31) + 1;
    }
  } else {
    // Para otros años (2024), se genera normalmente: mes 1-12 y día 1-28
    month = Math.floor(Math.random() * 12) + 1;
    day = Math.floor(Math.random() * 28) + 1;
  }
  const monthStr = month.toString().padStart(2, '0');
  const dayStr = day.toString().padStart(2, '0');
  return `${monthStr}/${dayStr}/${year}`;
};

// Generamos 120 reseñas: las primeras 60 con fecha aleatoria en 2024 y las siguientes 60 en 2025.
const reviews = Array.from({ length: 120 }, (_, i) => ({
  author: sampleNames[i % sampleNames.length],
  rating: 5,
  date: i < 60 ? getRandomDate(2024) : getRandomDate(2025),
  text: sampleComments[i % sampleComments.length]
}));

const GoogleReviews = () => {
  const reviewsPerPage = 3;
  const totalPages = Math.ceil(reviews.length / reviewsPerPage);
  const [currentPage, setCurrentPage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef(null);

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentPage((prevPage) => (prevPage + 1) % totalPages);
        setIsAnimating(false);
      }, 1000);
    }, 5000);
    return () => {
      resetTimeout();
    };
  }, [currentPage, totalPages]);

  const currentReviews = reviews.slice(
    currentPage * reviewsPerPage,
    currentPage * reviewsPerPage + reviewsPerPage
  );

  const handleIndicatorClick = (pageIndex) => {
    if (pageIndex === currentPage) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentPage(pageIndex);
      setIsAnimating(false);
      resetTimeout();
    }, 1000);
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px) scale(0.95); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes fadeOut {
            from { opacity: 1; transform: translateY(0) scale(1); }
            to { opacity: 0; transform: translateY(-20px) scale(0.95); }
          }
          .fade-in {
            animation: fadeIn 1s ease-in forwards;
          }
          .fade-out {
            animation: fadeOut 1s ease-out forwards;
          }
          .indicators {
            display: flex;
            justify-content: center;
            gap: 10px;
            margin-top: 20px;
          }
          .indicator-dot {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background-color: #000;
            cursor: pointer;
            transition: background-color 0.3s, transform 0.3s;
          }
          .indicator-dot.inactive {
            background-color: #ccc;
          }
          .indicator-dot.active {
            background-color: #000;
          }
          .indicator-dot:hover {
            transform: scale(1.2);
          }
          .card:hover {
            transform: translateY(-10px) scale(1.02);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3);
          }
        `}
      </style>

      <div style={styles.content}>
        <h2 style={styles.heading}>
          <GoogleGLogo />
          What Our Clients Say
        </h2>

        {/* Descripción resumida */}
        <div style={styles.descriptionContainer}>
          <p style={styles.description}>
            Jet Black Limo offers luxury transportation across New York, ensuring a reliable, safe, and punctual experience for airport transfers, corporate events, and more.
          </p>
        </div>

        <div style={styles.grid}>
          {currentReviews.map((review, index) => (
            <div
              key={index}
              className={`card ${isAnimating ? 'fade-out' : 'fade-in'}`}
              style={styles.card}
            >
              <div style={styles.reviewHeader}>
                <div style={styles.avatar}>
                  <GoogleGLogo />
                </div>
                <div>
                  <h3 style={styles.author}>{review.author}</h3>
                  <p style={styles.date}>{review.date}</p>
                </div>
              </div>
              <p style={styles.rating}>{'⭐'.repeat(review.rating)}</p>
              <p style={styles.text}>{review.text}</p>
            </div>
          ))}
        </div>

        <div className="indicators">
          {Array.from({ length: totalPages }).map((_, index) => (
            <div
              key={index}
              className={`indicator-dot ${currentPage === index ? 'active' : 'inactive'}`}
              onClick={() => handleIndicatorClick(index)}
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
    minHeight: '100vh',
    margin: '0',
    padding: '20px',
    fontFamily: "'Arial', sans-serif",
    backgroundColor: '#ffffff',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  content: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '40px 20px',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  heading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '36px',
    marginBottom: '20px',
    color: '#000000',
  },
  icon: {
    marginRight: '10px',
  },
  descriptionContainer: {
    textAlign: 'center',
    marginBottom: '40px',
    width: '100%',
  },
  description: {
    fontSize: '16px',
    color: '#000000',
    marginBottom: '15px',
    lineHeight: '1.6',
    maxWidth: '800px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  grid: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    flexWrap: 'wrap',
    width: '100%',
    maxWidth: '1200px',
    marginBottom: '20px',
  },
  card: {
    background: '#ffffff',
    borderRadius: '10px',
    padding: '20px',
    width: '300px',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    transition: 'transform 0.3s, box-shadow 0.3s',
  },
  reviewHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
  },
  avatar: {
    backgroundColor: '#ffffff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '15px',
    flexShrink: 0,
    width: '50px',
    height: '50px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  },
  author: {
    fontSize: '18px',
    fontWeight: 'bold',
    margin: 0,
    color: '#007BFF',
  },
  date: {
    fontSize: '14px',
    color: '#666',
    margin: 0,
  },
  rating: {
    color: '#FFA500',
    fontSize: '18px',
    marginBottom: '10px',
  },
  text: {
    fontSize: '16px',
    color: '#333',
    lineHeight: '1.5',
  },
};

export default GoogleReviews;
